/**
 * The loading placeholder for one job card, in the dcv2 design language.
 *
 * It mirrors JobCard's geometry exactly — same 18px radius, same .13 hairline,
 * same 22px padding, same 46px glyph tile — so the board does not reflow when
 * the fetch lands. The shimmer uses the app-wide `pulseDot` keyframe, which
 * animates opacity only; the canvas's reduced-motion rule kills it and leaves
 * the bars at rest.
 */

export function SkeletonBar({
  width,
  height = 12,
  radius = 6,
  delay = 0,
  style,
}: {
  width: number | string;
  height?: number;
  radius?: number | string;
  delay?: number;
  style?: React.CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "block",
        width,
        height,
        borderRadius: radius,
        background: "rgba(var(--ink-rgb),.09)",
        animation: `pulseDot 1.8s ${delay}s ease-in-out infinite`,
        ...style,
      }}
    />
  );
}

export function JobCardSkeleton() {
  return (
    <div
      style={{
        background: "var(--paper2)",
        border: "1px solid rgba(var(--ink-rgb),.13)",
        borderRadius: 18,
        padding: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
        <SkeletonBar width={46} height={46} radius={9} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 14,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 8 }}>
              <SkeletonBar width="62%" height={16} delay={0.05} />
              <SkeletonBar width={140} height={11} delay={0.1} />
            </div>
            <SkeletonBar width={34} height={34} radius={9} delay={0.15} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 16 }}>
            <SkeletonBar width={96} height={11} delay={0.2} />
            <SkeletonBar width={80} height={11} delay={0.25} />
            <SkeletonBar width={64} height={11} delay={0.3} />
          </div>
          <div style={{ display: "grid", gap: 7, marginTop: 16 }}>
            <SkeletonBar width="100%" delay={0.35} />
            <SkeletonBar width="82%" delay={0.4} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
            <SkeletonBar width={104} height={28} radius={999} delay={0.45} />
            <SkeletonBar width={118} height={28} radius={999} delay={0.5} />
            <SkeletonBar width={72} height={13} delay={0.55} style={{ marginLeft: "auto" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
